import { isNotNullOrUndefined } from '../../../shared/util/collection/isNotNullOrUndefined';
import { ShelfMappingResponse } from '../../store/StoreService.model';
import { ComposableDeviceControlUnitMap } from '../CommanderAirConnectAPI/CommanderAirConnectAPI.model';
import { DeviceControlUnitsAPIResponse } from '../device.model';

/**
 * A control unit resolved by its ST bus address (cuaddr).
 *
 * The ST bus address is the primary, front-end facing identifier. The backend
 * control unit id (cuid) is kept alongside because backend contracts
 * (translations, sensor/monitoring data, alarms, reports) are still keyed by it.
 */
export interface ControlUnitByBusAddress {
  /** ST bus address (cuaddr), e.g. "8". Primary identifier going forward. */
  busAddress: string;
  /**
   * Backend control unit id (cuid). Kept for backend boundary calls only.
   * May be undefined if the composable map has no id yet.
   */
  controlUnitId?: string;
  /** Latest live control unit data from the store, when it has been loaded. */
  latest?: DeviceControlUnitsAPIResponse;
}

/** Control units indexed by their ST bus address (cuaddr). */
export type ControlUnitsByBusAddress = Record<string, ControlUnitByBusAddress>;

/**
 * Build the composable control unit anchor (cuaddr + cuid pairs) from a
 * device's `shelfMappings`. Use this as a fallback when the richer composable
 * device `controlUnits` are not available (e.g. a plain store device).
 *
 * @param shelfMappings The device's shelf mappings.
 * @param deviceId      The owning device/commander id, kept as the unique key.
 */
export function composableControlUnitsFromShelfMappings(
  shelfMappings: Array<ShelfMappingResponse | null> | undefined,
  deviceId: string
): ComposableDeviceControlUnitMap[] {
  return (shelfMappings ?? [])
    .filter(isNotNullOrUndefined)
    .map((shelfMapping) => ({
      deviceId,
      controlUnitBusAddress: shelfMapping.cuaddr,
      controlUnitId: `${shelfMapping.cuid}`,
    }));
}

/**
 * Build a structure that maps the ST bus address (cuaddr) coming from a
 * composable device to the latest live control unit data from the store.
 *
 * The composable device's `controlUnits` are used as the anchor because each
 * entry already pairs the ST bus address (`controlUnitBusAddress`) with the
 * backend id (`controlUnitId`). The latest live control unit is matched by
 * cuid first and falls back to matching by the live unit's `address`.
 *
 * @param composableControlUnits The `controlUnits` of a composable device.
 * @param latestControlUnits     The latest live control units from the store.
 */
export function getControlUnitsByBusAddress(
  composableControlUnits: ComposableDeviceControlUnitMap[] | undefined,
  latestControlUnits: DeviceControlUnitsAPIResponse[] | undefined
): ControlUnitsByBusAddress {
  const latestByAddress = new Map(
    (latestControlUnits ?? []).map((unit) => [unit.address, unit])
  );
  const latestById = new Map(
    (latestControlUnits ?? []).map((unit) => [unit.id, unit])
  );

  return (composableControlUnits ?? [])
    .filter(isNotNullOrUndefined)
    .reduce<ControlUnitsByBusAddress>((acc, controlUnitMap) => {
      const controlUnitAddress = controlUnitMap.controlUnitAddress;
      const latest =
        (controlUnitMap.controlUnitId
          ? latestById.get(controlUnitMap.controlUnitId)
          : undefined) ??
        (controlUnitAddress
          ? latestByAddress.get(controlUnitAddress)
          : undefined);

      // If not busADdress is founded in composable Device.
      // Use the address based on the latest control unit data.
      // This is a fallback for the case where the composable device has no bus address yet.
      const busAddress = controlUnitAddress ?? latest?.address;
      if (!busAddress) {
        return acc;
      }

      acc[busAddress] = {
        busAddress,
        controlUnitId: latest?.id ?? controlUnitMap.controlUnitId,
        latest: latest ?? latestByAddress.get(busAddress),
      };
      return acc;
    }, {});
}

/**
 * Resolve the backend control unit id (cuid) for a given ST bus address.
 * Use this at backend boundaries that still require cuid.
 */
export function getControlUnitIdByBusAddress(
  controlUnitsByBusAddress: ControlUnitsByBusAddress,
  busAddress: string | undefined
): string | undefined {
  if (!busAddress) return undefined;
  return controlUnitsByBusAddress[busAddress]?.controlUnitId;
}

/**
 * Resolve the ST bus address (cuaddr) for a given backend control unit id.
 * Use this when ingesting backend responses that are keyed by cuid.
 */
export function getBusAddressByControlUnitId(
  controlUnitsByBusAddress: ControlUnitsByBusAddress,
  controlUnitId: string | undefined
): string | undefined {
  if (!controlUnitId) return undefined;
  return Object.values(controlUnitsByBusAddress).find(
    (controlUnit) => controlUnit.controlUnitId === controlUnitId
  )?.busAddress;
}
